import React, { useState, useCallback } from 'react';
import { useRecoilState } from 'recoil';
import {
  Box,
  Heading,
  FormControl,
  FormLabel,
  Select,
  HStack,
  Button,
  Tooltip,
  Icon,
  IconButton,
  useDisclosure
} from '@chakra-ui/react';
import { InfoIcon } from '@chakra-ui/icons';
import { formDataState } from '../../state/calculatorState';
import { ucsValues, ddiValues } from '../../utils/complexityCalculator';
import { DDITrajectoryModal } from './DDITrajectoryModal';

const CalculatorIcon = (props) => (
  <Icon viewBox="0 0 24 24" {...props}>
    <path
      fill="currentColor"
      d="M7 2h10a2 2 0 012 2v16a2 2 0 01-2 2H7a2 2 0 01-2-2V4a2 2 0 012-2zm0 2v4h10V4H7zm0 6v2h2v-2H7zm4 0v2h2v-2h-2zm4 0v2h2v-2h-2zm-8 4v2h2v-2H7zm4 0v2h2v-2h-2zm4 0v6h2v-6h-2zm-8 4v2h2v-2H7zm4 0v2h2v-2h-2z"
    />
  </Icon>
);

const TOOLTIPS = {
  ucs: 'Unconfined Compressive Strength of the predominant rock formation',
  interbedded: 'Formation contains interbedded layers of varying hardness',
  ddi: 'Directional Difficulty Index - measure of well trajectory complexity',
  multilateral: 'Well has multiple lateral branches from main wellbore',
  interception: 'Well requires precise interception of existing wellbore or target'
};

const LabelWithInfo = ({ children, tooltip }) => (
  <HStack spacing={1}>
    <span>{children}</span>
    <Tooltip label={tooltip} placement="top" hasArrow>
      <span><Icon as={InfoIcon} boxSize={3} color="gray.400" cursor="help" /></span>
    </Tooltip>
  </HStack>
);

// Filter out 'calculated' from dropdown options
const ddiSelectValues = Object.fromEntries(
  Object.entries(ddiValues).filter(([key]) => key !== 'calculated')
);

export const DrillingComplexitySection = ({ compact }) => {
  const [formData, setFormData] = useRecoilState(formDataState);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [ddiHighlight, setDdiHighlight] = useState(null);

  const DDI_HIGHLIGHT_COLORS = {
    verylow: 'green.200',
    low: 'yellow.200',
    medium: 'orange.200',
    high: 'red.200',
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleBoolToggle = (field) => {
    setFormData(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const handleDDIApply = useCallback((category, rawValue) => {
    setFormData(prev => ({
      ...prev,
      ddi: category,
      ddiCalculatedValue: rawValue
    }));
    setDdiHighlight(category);
    setTimeout(() => setDdiHighlight(null), 1000);
  }, [setFormData]);

  return (
    <Box bg="white" p={compact ? 0 : 5} borderRadius={compact ? 0 : 'lg'} boxShadow={compact ? 'none' : 'sm'}>
      {!compact && <Heading size="md" mb={4}>Drilling Complexity</Heading>}

      <FormControl mb={3}>
        <FormLabel fontSize="sm" fontWeight="semibold">
          <LabelWithInfo tooltip={TOOLTIPS.ucs}>Predominant UCS</LabelWithInfo>
        </FormLabel>
        <Select size="sm" value={formData.ucs} onChange={(e) => handleChange('ucs', e.target.value)}>
          <option value="" disabled>Select...</option>
          {Object.entries(ucsValues).map(([key, { txt }]) => (
            <option key={key} value={key}>{txt}</option>
          ))}
        </Select>
      </FormControl>

      <Tooltip label={TOOLTIPS.interbedded} placement="top" hasArrow>
        <Button
          size="xs"
          mb={3}
          role="switch"
          aria-checked={formData.interbedded}
          variant={formData.interbedded ? 'solid' : 'outline'}
          colorScheme={formData.interbedded ? 'blue' : 'gray'}
          onClick={() => handleBoolToggle('interbedded')}
        >
          Interbedded
        </Button>
      </Tooltip>

      <FormControl mb={3}>
        <FormLabel fontSize="sm" fontWeight="semibold">
          <LabelWithInfo tooltip={TOOLTIPS.ddi}>DDI</LabelWithInfo>
        </FormLabel>
        <HStack spacing={1}>
          <Select
            size="sm"
            flex={1}
            value={formData.ddi}
            onChange={(e) => handleChange('ddi', e.target.value)}
            bg={ddiHighlight ? DDI_HIGHLIGHT_COLORS[ddiHighlight] || 'yellow.200' : 'white'}
            transition="background-color 1.5s ease"
          >
            <option value="" disabled>Select...</option>
            {Object.entries(ddiSelectValues).map(([key, { txt }]) => (
              <option key={key} value={key}>{txt}</option>
            ))}
          </Select>
          <Tooltip label="Calculate DDI" placement="top" hasArrow>
            <IconButton
              icon={<CalculatorIcon boxSize={4} />}
              size="sm"
              variant="outline"
              colorScheme="blue"
              aria-label="Calculate DDI"
              onClick={onOpen}
            />
          </Tooltip>
        </HStack>
      </FormControl>

      <DDITrajectoryModal
        isOpen={isOpen}
        onClose={onClose}
        onApply={handleDDIApply}
        units={formData.units}
      />

      <HStack spacing={2}>
        <Tooltip label={TOOLTIPS.multilateral} placement="top" hasArrow>
          <Button size="xs" role="switch" aria-checked={formData.multilateral} variant={formData.multilateral ? 'solid' : 'outline'} colorScheme={formData.multilateral ? 'blue' : 'gray'} onClick={() => handleBoolToggle('multilateral')}>
            Multilateral
          </Button>
        </Tooltip>
        <Tooltip label={TOOLTIPS.interception} placement="top" hasArrow>
          <Button size="xs" role="switch" aria-checked={formData.interception} variant={formData.interception ? 'solid' : 'outline'} colorScheme={formData.interception ? 'blue' : 'gray'} onClick={() => handleBoolToggle('interception')}>
            Interception
          </Button>
        </Tooltip>
      </HStack>
    </Box>
  );
};

export default DrillingComplexitySection;
